import { formatNombre, formatMois } from '../../lib/formats';

/**
 * Aides communes aux rapports mensuels: période, onglets, mise en forme des
 * chiffres et libellés complets des colonnes. Rien ici n'affiche un code.
 */

/** Libellés complets des colonnes abrégées, par clé de colonne. */
const LIBELLES_COLONNES = {
  rotations: 'Rotations',
  advance_received: 'Avance reçue',
  payment_received: 'Paiement reçu',
  revenue: 'Facture transport',
  insurance: 'Assurance',
  other_fees: 'Autres frais',
  total_operating: "Total charges d'exploitation",
  admin_papers: 'Renouvellement des papiers',
  total_maintenance: 'Total maintenance',
  total_cost: 'Coût total des charges',
  notes: 'Observations',
  capacity_liters: 'Capacité',
};

/** Libellé affiché d'une colonne: la version complète si elle existe. */
export const libelleColonne = (colonne) => LIBELLES_COLONNES[colonne.key] || colonne.label;

const LIBELLES_GROUPES = {
  'Charge Maintenance': 'Maintenance',
  'Charge fixe transport': 'Charges fixes',
};

export const libelleGroupe = (texte) => LIBELLES_GROUPES[texte] || texte;

export const ONGLETS = [
  { value: 'compte', label: "Compte d'exploitation" },
  { value: 'synthese', label: 'Synthèse par camion' },
  { value: 'loues', label: 'Camions loués' },
  { value: 'bilan', label: 'Bilan du mois' },
];

export const ONGLET_PAR_DEFAUT = 'compte';

export const ongletValide = (valeur) =>
  ONGLETS.some((onglet) => onglet.value === valeur) ? valeur : ONGLET_PAR_DEFAUT;

/* ------------------------------------------------------------------------ */
/* Période                                                                   */
/* ------------------------------------------------------------------------ */

const deuxChiffres = (n) => String(n).padStart(2, '0');

export const moisCourant = (maintenant = new Date()) => ({
  annee: maintenant.getFullYear(),
  mois: maintenant.getMonth() + 1,
});

/** « 2026-05 » */
export const cleMois = ({ annee, mois }) => `${annee}-${deuxChiffres(mois)}`;

/** Lit « 2026-05 » (ou rien). Renvoie `null` si ce n'est pas un mois valide. */
export const lireMois = (texte) => {
  const trouve = /^(\d{4})-(\d{1,2})$/.exec(String(texte || '').trim());
  if (!trouve) return null;
  const annee = Number(trouve[1]);
  const mois = Number(trouve[2]);
  if (annee < 2000 || annee > 2100 || mois < 1 || mois > 12) return null;
  return { annee, mois };
};

export const decaler = ({ annee, mois }, pas) => {
  const index = annee * 12 + (mois - 1) + pas;
  return { annee: Math.floor(index / 12), mois: (index % 12) + 1 };
};

export const comparerMois = (a, b) => a.annee * 12 + a.mois - (b.annee * 12 + b.mois);

/** « mai 2026 » */
export const libelleMois = (periode) => formatMois(cleMois(periode));

/** Premier jour du mois suivant, en date de calendrier (« 2026-06-01 »). */
export const premierDuMoisSuivant = (periode) => {
  const suivant = decaler(periode, 1);
  return `${suivant.annee}-${deuxChiffres(suivant.mois)}-01`;
};

/* ------------------------------------------------------------------------ */
/* Chiffres                                                                  */
/* ------------------------------------------------------------------------ */

export const estNombre = (valeur) =>
  valeur !== null && valeur !== undefined && valeur !== '' && Number.isFinite(Number(valeur));

/** Nombre à la française, 2 décimales seulement s'il en reste (autre devise). */
export const nombre = (valeur) => {
  if (!estNombre(valeur)) return '';
  const n = Number(valeur);
  return formatNombre(n, Number.isInteger(n) ? 0 : 2);
};

export const estNegatif = (valeur) => estNombre(valeur) && Number(valeur) < 0;

/** Classe de texte d'un montant: rouge sobre quand il est négatif. */
export const classeMontant = (valeur) => (estNegatif(valeur) ? 'text-destructive' : '');

/** « 1 saisie » / « 3 saisies » */
export const pluriel = (n, singulier, plurielTexte) => (Number(n) > 1 ? plurielTexte : singulier);

/* ------------------------------------------------------------------------ */
/* Catégories de charges (barres empilées, légende, chronologie)             */
/* ------------------------------------------------------------------------ */

/**
 * Les charges d'un camion, dans l'ordre du compte d'exploitation. La couleur
 * n'est jamais seule: la légende et la chronologie donnent toujours le nom.
 */
export const CATEGORIES_CHARGES = [
  { key: 'salary', label: 'Salaire', couleur: 'hsl(207 70% 45%)' },
  { key: 'insurance', label: 'Assurance', couleur: 'hsl(188 62% 40%)' },
  { key: 'road_fees', label: 'Frais de route', couleur: 'hsl(30 80% 50%)' },
  { key: 'other_fees', label: 'Frais supplémentaires', couleur: 'hsl(45 85% 48%)' },
  { key: 'repair', label: 'Entretien et réparation', couleur: 'hsl(262 50% 58%)' },
  { key: 'parts', label: 'Pièces détachées', couleur: 'hsl(292 38% 55%)' },
  { key: 'admin_papers', label: 'Renouvellement des papiers', couleur: 'hsl(335 55% 55%)' },
  { key: 'rent', label: 'Fret', couleur: 'hsl(20 45% 45%)' },
  { key: 'depreciation', label: 'Amortissement', couleur: 'hsl(215 14% 58%)' },
];

/** Encaissements donnés à titre d'information (ne s'ajoutent pas aux recettes). */
export const ENCAISSEMENTS = [
  { key: 'advance_received', label: 'Avance reçue' },
  { key: 'payment_received', label: 'Paiement reçu' },
];

export const montant = (valeur) => (estNombre(valeur) ? Number(valeur) : 0);

/** Nom du stock au bilan: sans dépôt, le serveur l'appelle « Carburant chargé, non livré ». */
export const libelleStock = (stock) => stock?.short_label || 'Carburant en stock';

/** Parts non nulles d'un ensemble de montants, pour une barre empilée. */
export const partsCharges = (montants) =>
  CATEGORIES_CHARGES.map((categorie) => ({ ...categorie, valeur: montant(montants?.[categorie.key]) })).filter(
    (part) => part.valeur > 0,
  );

/** Marge en pour cent (résultat / recettes), `null` sans recette. */
export const margePourcent = (recettes, resultat) => {
  const r = montant(recettes);
  if (r <= 0) return null;
  return (montant(resultat) / r) * 100;
};

/** « 12 % », « −4,5 % ». */
export const formatPourcentSigne = (valeur, { signe = false } = {}) => {
  if (valeur === null || valeur === undefined || !Number.isFinite(valeur)) return '';
  const arrondi = Math.abs(valeur) >= 10 ? Math.round(valeur) : Math.round(valeur * 10) / 10;
  const absolu = formatNombre(Math.abs(arrondi), Number.isInteger(arrondi) ? 0 : 1);
  const prefixe = arrondi < 0 ? '−' : signe && arrondi > 0 ? '+' : '';
  return `${prefixe}${absolu} %`;
};

/** Variation relative entre deux montants, `null` si le mois précédent vaut zéro. */
export const variationPourcent = (courant, precedent) => {
  const p = montant(precedent);
  if (!estNombre(courant) || p === 0) return null;
  return ((montant(courant) - p) / Math.abs(p)) * 100;
};

export const FRET_NON_RENSEIGNE = 'Fret non renseigné';
const LIBELLES_FRET = {
  PREVU: 'Prévu',
  INTERNE: 'Fret interne',
  REFERENCE: 'Valeur de référence',
  NON_RENSEIGNE: FRET_NON_RENSEIGNE,
};
const TITRES_FRET = {
  'Valeur de référence': 'Valeur de référence, pas une facture.',
  'Fret interne': 'Transport pour le compte de la société, sans facture.',
  [FRET_NON_RENSEIGNE]: "Le prix du transport n'est pas renseigné: la recette est incomplète.",
};

/**
 * Signalements d'une ligne ou d'un camion, d'après le contrat du serveur:
 * `freight_status` (ligne), `freight_missing_count` et `freight_reference_count`
 * (camion). Un champ absent ne signale rien. « Prévu » n'est pas répété quand
 * la ligne porte déjà sa pastille `estimated`.
 */
export const signalements = (objet) => {
  if (!objet || typeof objet !== 'object') return [];
  const liste = [];
  const ajouter = (texte) => {
    if (texte && !liste.includes(texte)) liste.push(texte);
  };
  const statut = LIBELLES_FRET[objet.freight_status];
  if (statut && !(objet.freight_status === 'PREVU' && objet.estimated)) ajouter(statut);
  if (Number(objet.freight_missing_count) > 0) ajouter(FRET_NON_RENSEIGNE);
  if (Number(objet.freight_reference_count) > 0) ajouter(LIBELLES_FRET.REFERENCE);
  return liste;
};

/** Info-bulle d'un signalement: pour une valeur de référence, la note de la ligne. */
export const titreSignalement = (objet, texte) => {
  const notes = (objet?.notes || []).filter(Boolean).map(String).join(' ; ');
  if (texte === LIBELLES_FRET.REFERENCE && notes) return notes;
  return TITRES_FRET[texte];
};

/** Contrôle des recettes du rapport (`revenue_check`): textes à afficher. */
export const messagesControleRecettes = (rapport) => {
  const controle = rapport?.revenue_check;
  if (!controle || typeof controle !== 'object') return [];
  const messages = [];
  // Des litres vendus sans coût d'achat connu: le message du serveur le dit.
  if (controle.complete === false || montant(controle.fuel_cost_unknown_liters) > 0) {
    (Array.isArray(controle.messages) ? controle.messages : []).forEach((m) => {
      if (m && typeof m === 'string' && !messages.includes(m)) messages.push(m);
    });
  }
  if (controle.no_rotation) messages.push('Aucune rotation ce mois-ci');
  return messages;
};

/** « 3 camions actifs sur 5 » quand le groupe donne les deux effectifs. */
export const libelleEffectif = (groupe) => {
  const actifs = groupe?.active_trucks_count ?? groupe?.trucks_count ?? groupe?.subtotal?.trucks_count;
  const flotte = groupe?.fleet_trucks_count ?? groupe?.subtotal?.fleet_trucks_count;
  if (!estNombre(actifs) || !estNombre(flotte)) return '';
  return `${actifs} ${pluriel(actifs, 'camion actif', 'camions actifs')} sur ${flotte}`;
};

/**
 * Pagination d'un rapport en sections (groupes de flotte, propriétaires).
 * `entrees`: la liste à plat, dans l'ordre d'affichage, où chaque entrée porte
 * `section` (clé de sa section) et `rang` (sa position, de 0, dans sa section).
 * Rend, pour une page, les sections qu'elle touche: `{ cle, entrees }`. Une
 * section coupée entre deux pages reparaît en tête de la suivante.
 */
export const sectionsDeLaPage = (entrees) => {
  const sections = [];
  (entrees || []).forEach((entree) => {
    const derniere = sections[sections.length - 1];
    if (derniere && derniere.cle === entree.section) derniere.entrees.push(entree);
    else sections.push({ cle: entree.section, entrees: [entree] });
  });
  return sections;
};

/** « Camions 21 à 40 sur 85 » quand la page ne montre qu'une partie de la section; sinon rien. */
export const tranchePartielle = (entrees, total, mots) => {
  if (!entrees.length || entrees.length >= total) return null;
  const premier = entrees[0].rang + 1;
  const dernier = entrees[entrees.length - 1].rang + 1;
  return `${mots[0].toUpperCase()}${mots.slice(1)} ${premier} à ${dernier} sur ${total}`;
};
